import React, { useState } from 'react';

const DuplicateResolutionModal = ({ duplicates, onResolve, onCancel }) => {
  const [visible, setVisible] = useState(true);
  const [selections, setSelections] = useState(() => {
    // Initialize selections with defaults (most recent)
    const initial = { csv: {}, xml: {} };
    
    Object.entries(duplicates).forEach(([sampleId, data]) => {
      if (data.csv) {
        initial.csv[sampleId] = data.csv.selected;
      }
      if (data.xml) {
        initial.xml[sampleId] = {};
        Object.entries(data.xml).forEach(([fieldName, fieldData]) => {
          initial.xml[sampleId][fieldName] = fieldData.selected;
        });
      }
    });
    
    return initial;
  });

  const handleCsvChange = (sampleId, index) => {
    setSelections(prev => ({
      ...prev,
      csv: {
        ...prev.csv,
        [sampleId]: parseInt(index)
      }
    }));
  };

  const handleXmlChange = (sampleId, fieldName, index) => {
    setSelections(prev => ({
      ...prev,
      xml: {
        ...prev.xml,
        [sampleId]: {
          ...(prev.xml[sampleId] || {}),
          [fieldName]: parseInt(index)
        }
      }
    }));
  };

  const handleResolve = () => {
    setVisible(false);
    setTimeout(() => {
      onResolve(selections);
    }, 300);
  };

  const handleCancel = () => {
    setVisible(false);
    setTimeout(() => {
      onCancel();
    }, 300);
  };

  const formatFieldName = (fieldName) => {
    // Convert field names to readable format
    const fieldMap = {
      'ibu': 'Bitterness (IBU)',
      'color': 'Color (SRM)',
      'carbohydrates': 'Carbohydrates (g/L)',
      'calcium': 'Calcium (ppm)',
      'magnesium': 'Magnesium (ppm)',
      'bicarbonate': 'Bicarbonate (ppm)',
      'chloride': 'Chloride (ppm)',
      'sulfate': 'Sulfate (ppm)',
      'sodium': 'Sodium (ppm)',
      'potassium': 'Potassium (ppm)',
      'zinc': 'Zinc (ppm)',
      'alkalinity': 'Alkalinity (ppm)'
    };
    
    return fieldMap[fieldName] || fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  };

  return (
    <div className={`announcement-modal-overlay ${visible ? 'visible' : ''}`}>
      <div className="announcement-modal" style={{ maxWidth: '700px' }}>
        <div className="announcement-modal-header">
          <h2 className="announcement-modal-title">⚠️ Duplicate Test Results Found</h2>
          <button 
            className="announcement-modal-close" 
            onClick={handleCancel}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        <div className="announcement-modal-content">
          <p style={{ marginBottom: '20px' }}>
            Multiple test results were found for some samples. Please select which results to use.
          </p>
          
          {Object.entries(duplicates).map(([sampleId, data]) => (
            <div key={sampleId} style={{ marginBottom: '20px' }}>
              {/* CSV Duplicates */}
              {data.csv && (
                <div className="sample-item margin-bottom-md">
                  <label>Sample #{sampleId} - Alcolyzer Data</label>
                  <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#767676', marginBottom: '8px' }}>
                    {data.csv.sets.length} test sets found
                  </div>
                  <select 
                    className="form-input"
                    value={selections.csv[sampleId] || data.csv.selected}
                    onChange={(e) => handleCsvChange(sampleId, e.target.value)}
                  >
                    {data.csv.sets.map((set, index) => (
                      <option key={index} value={index}>
                        {set.timestamp}
                        {set.is_most_recent ? ' (Most Recent)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* XML Duplicates */}
              {data.xml && Object.entries(data.xml).map(([fieldName, fieldData]) => (
                <div key={fieldName} className="sample-item margin-bottom-md">
                  <label>Sample #{sampleId} - {formatFieldName(fieldName)}</label>
                  <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#767676', marginBottom: '8px' }}>
                    {fieldData.values.length} results found
                  </div>
                  <div className="batch-detail">
                    {fieldData.values.map((val, index) => (
                      <div key={index} style={{ marginBottom: '8px' }}>
                        <label style={{ fontWeight: 'normal', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input 
                            type="radio" 
                            name={`xml_${sampleId}_${fieldName}`}
                            checked={selections.xml[sampleId]?.[fieldName] === index || (!selections.xml[sampleId]?.[fieldName] && index === fieldData.selected)}
                            onChange={() => handleXmlChange(sampleId, fieldName, index)}
                            style={{ marginRight: '8px' }}
                          />
                          <span>
                            {val.value} {val.timestamp ? `(${val.timestamp})` : ''}
                            {val.is_most_recent && <span style={{ marginLeft: '8px', color: '#f49b1b', fontStyle: 'italic' }}>Most Recent</span>}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        
        <div className="announcement-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button className="back button-link" onClick={handleCancel}>
            Cancel
          </button>
          <button className="submit" onClick={handleResolve}>
            Use Selected Results
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateResolutionModal;
